/**
 * Stakeholder ordering and normalization — **exact strings** match legacy mobile
 * `buxus-frontend` `ALL_STAKEHOLDER_TYPES` / CRM (ET/FM/SC prefixes, not T2/FA/SR).
 * {@link normalizeStakeholderCode} still accepts legacy aliases (e.g. `T2 - Tier II` → `ET - Tier II`).
 */

/** Fixed menu order for stakeholder switcher (ER first, Other last). */
export const ALL_STAKEHOLDER_TYPES_ORDER: readonly string[] = [
  "ER - Emergency Responder",
  "EC - Commander",
  "ET - Tier II",
  "EX - Excavator",
  "FM - Farmer",
  "PO - Public Official",
  "AP - Affected Public",
  "SC - School Rep",
  "OT - Other",
]

/** Stakeholder types that see the "All pipelines are not pictured" line on Information (legacy). */
export const RESPONDER_STAKEHOLDER_TYPES: readonly string[] = [
  "ET - Tier II",
  "EC - Commander",
  "ER - Emergency Responder",
]

/**
 * Order available codes: canonical menu order first, then unknown strings alphabetically.
 * Maps each backend key (including older aliases like `T2`/`FA`/`SR`) to its canonical CRM string
 * via {@link normalizeStakeholderCode}, preserving the **original** key for SAS / persistence.
 */
export function orderStakeholderCodes(available: readonly string[]): string[] {
  const canonToRaw = new Map<string, string>()
  const unknownRaw: string[] = []

  for (const raw of available) {
    const t = raw.trim()
    if (!t) continue
    const canon = normalizeStakeholderCode(t, ALL_STAKEHOLDER_TYPES_ORDER)
    if (canon) {
      if (!canonToRaw.has(canon)) canonToRaw.set(canon, t)
    } else {
      unknownRaw.push(t)
    }
  }

  const ordered = ALL_STAKEHOLDER_TYPES_ORDER.filter((c) => canonToRaw.has(c)).map(
    (c) => canonToRaw.get(c)!,
  )
  unknownRaw.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
  return [...ordered, ...unknownRaw]
}

/**
 * Same menu list as mobile when `availableStakeholderCodes.length > 0`:
 * only types that appear in BOTH the fixed list and the available set (fixed list order).
 * Unknown stakeholder strings from the backend are omitted (mobile parity).
 */
export function filterStakeholderMenuCodes(available: readonly string[]): string[] {
  if (available.length === 0) return []
  return orderStakeholderCodes(available).filter(
    (raw) => !!normalizeStakeholderCode(raw, ALL_STAKEHOLDER_TYPES_ORDER),
  )
}

export function normalizeStakeholderCode(
  rawCode: string | null | undefined,
  candidates: readonly string[],
): string | null {
  if (!rawCode || !rawCode.trim()) return null
  const incoming = rawCode.trim().toLowerCase()
  const exact = candidates.find((c) => c.trim().toLowerCase() === incoming)
  if (exact) return exact
  const tail = incoming.includes(" - ")
    ? incoming.split(" - ").slice(1).join(" - ")
    : incoming
  const byTail = candidates.find((c) => {
    const normalized = c.trim().toLowerCase()
    const normalizedTail = normalized.includes(" - ")
      ? normalized.split(" - ").slice(1).join(" - ")
      : normalized
    return normalizedTail === tail
  })
  if (byTail) return byTail

  /** Profile/session may store a legacy prefix while menu/catalog uses full CRM strings. */
  const byPrefix = candidates.find((c) => {
    const t = c.trim()
    const m = /^([A-Za-z0-9]{1,8})\s*-\s*/.exec(t)
    if (!m) return false
    return m[1].toLowerCase() === incoming
  })
  return byPrefix ?? null
}
